import React, { useEffect, useState } from 'react';

import { DATASTREAM } from '@/common/constants/config';

// Fetch Data from Datastream Server API
import getDataViaPostV2 from '../api/getDataViaPostV2';

type IRow = {
  displayname: string;
  firstmaildate: string;
  mailcode: string;
  mailinglistqty: number;
  parentlistqty: number;
  parentmailinglistdisplayname: string;
  parentmailinglistid: number;
  purchaseordernbr: string;
};

type PivotedRow = {
  firstmaildate: string;
  packages: Record<
    string,
    {
      mailcode: string;
      mailinglistqty: number;
      pct: string;
    }
  >;
  parentmailinglistdisplayname: string;
  parentmailinglistid: number;
  parentmlmnicename?: string; // Optional field for MLMN display name
  purchaseordernbr: string;
  totalqty: number;
};

type SummaryProps = {
  mailingid: number;
  view: string;
};

export default function MPSplitsReportGrid({ mailingid }: SummaryProps) {
  const [, setRowData] = useState<IRow[]>([]);
  const [tableData, setTableData] = useState<PivotedRow[]>([]);
  const [displayNames, setDisplayNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const url = `${DATASTREAM}/reports/v1/rpt-mp-mailing-splitdata/@i_mailing_id=${mailingid}`;

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);

      getDataViaPostV2(
        url,
        (response: any) => {
          // Check for expected structure
          const data = response?.rowData;

          if (!Array.isArray(data)) {
            setTableData([]); // clear any existing data
            setDisplayNames([]); // clear display columns
            setLoading(false);

            return;
          }

          setRowData(data);

          const pivoted: Record<number, PivotedRow> = {};
          const displays = new Set<string>();

          data.forEach((row) => {
            const id = row.parentmailinglistid;

            displays.add(row.displayname);

            if (!pivoted[id]) {
              pivoted[id] = {
                firstmaildate: row.firstmaildate,
                packages: {},
                parentmailinglistdisplayname: row.parentmailinglistdisplayname,
                parentmailinglistid: id,
                parentmlmnicename: row.parentmlmnicename || '', // Optional field for MLMN display name
                purchaseordernbr: row.purchaseordernbr || '',
                totalqty: row.parentlistqty,
              };
            }

            pivoted[id].packages[row.displayname] = {
              mailcode: row.mailcode,
              mailinglistqty: row.mailinglistqty,
              pct: row.parentlistqty ? ((row.mailinglistqty / row.parentlistqty) * 100).toFixed(0) : '0',
            };
          });

          setTableData(Object.values(pivoted));
          setDisplayNames([...displays]);
          setLoading(false);
        },
        {},
        () => {
          setTableData([]);
          setDisplayNames([]);
          setLoading(false);
        }
      );
    };

    fetchData();
  }, [url]);

  // -- Conditional rendering --

  const totalQty = tableData.reduce((sum, row) => {
    return sum + row.totalqty;
  }, 0);

  const packageTotals: Record<string, number> = {};

  displayNames.forEach((name) => {
    packageTotals[name] = tableData.reduce((sum, row) => {
      return sum + (row.packages[name]?.mailinglistqty || 0);
    }, 0);
  });

  if (loading) {
    return <div className="p-4 font-semibold text-blue-600">Loading split data…</div>;
  }

  if (tableData.length === 0) {
    return <div className="p-4 italic text-gray-500">No split data available for this mailing.</div>;
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-300 shadow-sm">
      <table className="min-w-full table-fixed border-separate border-spacing-0 text-left font-mono text-sm font-semibold">
        <thead>
          {/* Group Headers */}
          <tr>
            <th
              className="border border-gray-300 bg-blue-950 px-3 py-2 text-center font-sans font-bold text-white"
              colSpan={4}>
              Mailing Info
            </th>
            {displayNames.map((name) => {
              return (
                <th
                  className="border border-gray-300 bg-slate-900 px-3 py-2 text-center font-sans font-bold text-white"
                  colSpan={3}
                  key={name}>
                  {name}
                </th>
              );
            })}
          </tr>
          {/* Column Labels */}
          <tr className="font-semibold">
            <th className="border border-gray-300 bg-blue-900 px-3 py-2 text-center font-sans font-bold text-white">
              List Name
            </th>
            <th className="border border-gray-300 bg-blue-900 px-3 py-2 text-center font-sans font-bold text-white">
              Mail Date
            </th>
            <th className="border border-gray-300 bg-blue-900 px-3 py-2 text-center font-sans font-bold text-white">
              PO
            </th>
            <th className="border border-gray-300 bg-blue-900 px-3 py-2 text-center font-sans font-bold text-white">
              Total Qty
            </th>
            {displayNames.map((name) => {
              return (
                <React.Fragment key={name}>
                  <th className="border border-gray-300 bg-slate-400 px-3 py-2 text-center font-sans font-bold text-slate-950">
                    Code
                  </th>
                  <th className="border border-gray-300 bg-slate-400 px-3 py-2 text-center font-sans font-bold text-slate-950">
                    Qty
                  </th>
                  <th className="border border-gray-300 bg-slate-400 px-3 py-2 text-center font-sans font-bold text-slate-950">
                    %
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {tableData.map((row, idx) => {
            return (
              <tr
                className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                key={idx}>
                <td className="border border-gray-200 px-3 py-2">
                  <span className="font-extrabold text-slate-950">{row.parentmailinglistdisplayname}</span>
                  <br />
                  <span className="font-bold text-slate-700">{row.parentmlmnicename || ''}</span>
                </td>
                <td className="border border-gray-200 px-3 py-2 text-center font-bold text-slate-800">
                  {row.firstmaildate}
                </td>
                <td className="border border-gray-200 px-3 py-2 text-center">{row.purchaseordernbr}</td>
                <td className="border border-gray-200 bg-green-300 px-3 py-2 pr-2 text-center font-semibold text-green-700">
                  {row.totalqty.toLocaleString()}
                </td>
                {displayNames.map((name) => {
                  const pkg = row.packages[name] || { mailcode: '', mailinglistqty: 0, pct: '' };
                  const pctVal = Number.parseFloat(pkg.pct);
                  const pctColor = pctVal >= 60 ? 'bg-yellow-300' : pctVal >= 40 ? 'bg-yellow-200' : 'bg-yellow-100';

                  return (
                    <React.Fragment key={name}>
                      <td className="border border-gray-200 px-3 py-2 text-center">{pkg.mailcode}</td>
                      <td className="border border-gray-200 px-3 py-2 text-center">{pkg.mailinglistqty}</td>
                      <td className={`border border-gray-200 px-3 py-2 text-center ${pctColor}`}>{pkg.pct}%</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr className="bg-blue-100 font-semibold text-gray-700">
            <td
              className="border border-gray-300 px-3 py-2 text-left font-black"
              colSpan={3}>
              TOTAL
            </td>
            <td className="border border-gray-300 px-3 py-2 text-center font-black">{totalQty.toLocaleString()}</td>
            {displayNames.map((name) => {
              return (
                <React.Fragment key={name}>
                  <td
                    className="border border-gray-300 px-3 py-2 text-center font-black"
                    colSpan={3}>
                    {packageTotals[name].toLocaleString()}
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
